/**
 * Progress content for administrative care plan tasks (select-services,
 * approve-selected-services, finalize-care-plan-enrollment,
 * build-cocm-care-plan).
 *
 * These tasks are internal workflow steps that do not involve a member or
 * provider interaction, so logging progress should simply capture time + an
 * optional note + an optional follow-up — the "Quick Log" shape. The shared
 * `QuickLogFields` (also used by the TaskCard "Track" affordance) renders the
 * inputs. Submission flows through `logActivityProgress`, which (for
 * administrative activities) writes no Communication — only a Task note,
 * optional snooze, and a care-time Observation when minutes are entered.
 *
 * @agentic connections
 */

import { Alert, Button, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import {
  QuickLogFields,
  resolveFollowUpDate,
  type FollowUpKind,
} from "@/components/domain/care-time/QuickLogFields";
import type { ProgressContentProps } from "@/types/domain/progress-log";

/**
 * Quick-Log administrative progress form: note + Time Spent + follow-up
 * shortcuts. No reason select, no contact method.
 * @agentic connections
 */
export const AdministrativeQuickLogProgressContent = ({
  onSubmit,
  onCancel,
  onDirtyChange,
}: ProgressContentProps) => {
  const [followUpKind, setFollowUpKind] = useState<FollowUpKind>("none");
  const [customDate, setCustomDate] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [minutes, setMinutes] = useState<number | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDirty = notes.trim() !== "" || !!minutes || followUpKind !== "none";
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const handleSubmit = useCallback(async () => {
    const followUpDate = resolveFollowUpDate(followUpKind, customDate);
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        outcome: "general-progress",
        outcomeDisplay: "Progress Logged",
        notes: notes.trim() || undefined,
        snoozeBehavior: followUpDate ? { type: "prompt" } : { type: "none" },
        followUpDate,
        minutesLogged: minutes && minutes > 0 ? minutes : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to log progress");
    } finally {
      setSubmitting(false);
    }
  }, [followUpKind, customDate, notes, minutes, onSubmit]);

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Text size="sm">Log progress on this activity.</Text>

      <QuickLogFields
        followUpKind={followUpKind}
        onFollowUpKindChange={setFollowUpKind}
        customDate={customDate}
        onCustomDateChange={setCustomDate}
        notes={notes}
        onNotesChange={setNotes}
        minutes={minutes}
        onMinutesChange={setMinutes}
        disabled={submitting}
      />

      <Stack gap="xs">
        <Button
          onClick={handleSubmit}
          loading={submitting}
          color="purple"
          fullWidth
        >
          Log Progress
        </Button>
        <Button
          variant="subtle"
          onClick={onCancel}
          disabled={submitting}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
